// A refusal says what to do next, not only what went wrong (issue #47, P4): « out of range » names the Amplifier, « not
// enough resources » names what is missing and when the Draw comes, « not connected » names the relay still being
// built. Pure, so it is tested without a browser; net.ts appends it to the error toast.
import type { Command, Resource, Stock } from '@aurane/protocol';
import { BUILDING_COST, DECREE_COST_CREDITS, RESOURCE_LIST, UNIT_COST, type PlayerView } from '@aurane/sim';
import { t } from '../i18n/index.js';
import { minutesText } from './netstatus.js';

type V = Pick<PlayerView, 'me' | 'systems' | 'relays' | 'linkTargets' | 'time' | 'nextDrawAt' | 'timeScale'>;

/** The first resource short of `cost` in the best of these warehouses (null when one can pay or none is known). */
export function shortOf(stocks: readonly (Stock | null | undefined)[], cost: Partial<Record<Resource, number>>): { res: Resource; qty: number } | null {
  let best: { res: Resource; qty: number; total: number } | null = null;
  for (const st of stocks) {
    if (!st) continue;
    const miss = RESOURCE_LIST.map((r) => ({ res: r, qty: Math.ceil((cost[r] ?? 0) - st[r]) })).filter((x) => x.qty > 0);
    if (!miss.length) return null;
    const total = miss.reduce((n, x) => n + x.qty, 0);
    if (!best || total < best.total) best = { res: miss[0]!.res, qty: miss[0]!.qty, total };
  }
  return best && { res: best.res, qty: best.qty };
}

const fill = (s: string, p: Record<string, string | number>): string => s.replace(/\{(\w+)\}/g, (_, k: string) => String(p[k] ?? ''));

/** What to do about a refused command, or null when there is nothing more to say than the reason. */
export function remedyFor(reason: string, cmd: Command | null, v: V | null): string | null {
  const real = (sim: number): string => minutesText(Math.max(0, sim) / Math.max(1, v?.timeScale ?? 1));
  const draw = v ? real(v.nextDrawAt - v.time) : null;
  const stockOf = (id: string): Stock | null => v?.systems.find((s) => s.id === id)?.stock ?? null;
  const name = (id: string): string => v?.systems.find((s) => s.id === id)?.name ?? id;
  switch (reason) {
    case 'range': return t('remedyRange');
    case 'far': return t('remedyFar');
    case 'blackhole': return t('remedyBlackhole');
    case 'not enough resources': case 'not enough resources here': {
      if (!v || !cmd) return null;
      let miss: { res: Resource; qty: number } | null = null;
      if (cmd.type === 'build_relay') {
        const c = v.linkTargets[cmd.a]?.find((x) => x.to === cmd.b) ?? v.linkTargets[cmd.b]?.find((x) => x.to === cmd.a);
        if (c) miss = shortOf([stockOf(cmd.a), stockOf(cmd.b), stockOf(v.me.capital)], { metal: c.metal, energy: c.energy });
      } else if (cmd.type === 'build') miss = shortOf([stockOf(cmd.system)], BUILDING_COST[cmd.building]);
      else if (cmd.type === 'train') {
        const cost: Partial<Record<Resource, number>> = {};
        for (const r of RESOURCE_LIST) cost[r] = (UNIT_COST[cmd.unit][r] ?? 0) * cmd.count;
        miss = shortOf([stockOf(cmd.system)], cost);
      } else if (cmd.type === 'decree') return fill(t('remedyCredits'), { q: Math.ceil(DECREE_COST_CREDITS[cmd.kind] - v.me.credits), t: draw! });
      return miss ? fill(t('remedyMissing'), { q: miss.qty, r: t(miss.res), t: draw! }) : fill(t('remedyDraw'), { t: draw! });
    }
    case 'not connected to your network': case 'system not connected': {
      if (!v) return null;
      const ends = cmd?.type === 'build_relay' ? [cmd.a, cmd.b] : cmd && 'system' in cmd && typeof cmd.system === 'string' ? [cmd.system] : [];
      const building = v.relays.filter((r) => r.owner === v.me.id && !r.ready && (ends.includes(r.a) || ends.includes(r.b))).sort((a, b) => a.readyAt - b.readyAt)[0];
      if (building) { const end = ends.find((e) => e === building.a || e === building.b)!; return fill(t('remedyBuilding'), { s: name(end), t: real(building.readyAt - v.time) }); }
      return t('remedyConnect');
    }
    default: {
      if (!reason.startsWith('locked:') || !v) return null;
      const tier = Number(reason.slice(7));
      // Tiers 2 and 3 have a time floor counted from the founding; the Draw then checks the rest.
      const floor = tier === 2 ? 3600 : tier === 3 ? 6 * 3600 : tier === 5 ? 86400 : 0;
      const left = v.me.foundedAt + floor - v.time;
      return left > 0 ? fill(t('remedyLockedIn'), { t: real(left) }) : null;
    }
  }
}
